import axios from 'axios'
import queryString from 'query-string'
import {
  AssignCardFeedCardsRequest,
  AssignCardFeedCardsResponse,
  CardFeedEntriesResponse,
  CardFeedResponse,
  CardFeedsResponse,
  CardFeedStatus,
  CreateCardFeedRequest,
  UnassignCardFeedCardResponse,
  UpdateCardFeedRequest,
} from 'src/queries/cardFeeds/cardFeeds.models'
import { getRequestConfig } from 'src/queries/requestConfig'
import { PostPeopleInvitesRequest } from 'src/requests/postPeopleInvites.request'

const CARD_FEEDS_PAGE_SIZE = 100

// FE column field → server sort token. Only fields the server accepts are listed.
const SERVER_SORT_FIELD: Record<string, string> = {
  dateConnected: 'createdAt',
  cardProgramName: 'cardProgramName',
  cardNetwork: 'cardNetwork',
}

export interface CardFeedsQueryParams {
  sortField?: string
  sortDirection?: 'ASC' | 'DESC'
  statuses?: CardFeedStatus[]
  search?: string
  page?: number
  pageSize?: number
}

export const fetchCardFeeds = async (
  organizationId: string,
  params: CardFeedsQueryParams = {},
): Promise<CardFeedsResponse> => {
  const requestConfig = getRequestConfig()
  const {
    sortField,
    sortDirection,
    statuses,
    search,
    page = 0,
    pageSize = CARD_FEEDS_PAGE_SIZE,
  } = params
  // Drop any UI field the server doesn't accept rather than passing it through —
  // an unmapped field name would produce an invalid request. A direction is only
  // meaningful alongside a field, so gate it on a resolved server field too.
  const serverSortField = sortField ? SERVER_SORT_FIELD[sortField] : undefined
  const queryParams = queryString.stringify(
    {
      page,
      pageSize,
      ...(serverSortField && { sortField: serverSortField }),
      ...(serverSortField && sortDirection && { sortDirection }),
      ...(statuses && statuses.length > 0 && { status: statuses }),
      ...(search?.trim() && { search: search.trim() }),
    },
    { arrayFormat: 'none' },
  )
  const response = await axios.get<CardFeedsResponse>(
    `/organizations/${organizationId}/cardfeeds?${queryParams}`,
    requestConfig,
  )
  return response.data
}

export const fetchCardFeed = async (
  organizationId: string,
  cardFeedId: string,
): Promise<CardFeedResponse> => {
  const requestConfig = getRequestConfig()
  const response = await axios.get<CardFeedResponse>(
    `/organizations/${organizationId}/cardfeeds/${cardFeedId}`,
    requestConfig,
  )
  return response.data
}

export const fetchCardFeedCards = async (
  cardFeedId: string,
  page = 0,
): Promise<CardFeedEntriesResponse> => {
  const requestConfig = getRequestConfig()
  const params = queryString.stringify({
    page,
    pageSize: CARD_FEEDS_PAGE_SIZE,
  })
  const response = await axios.get<CardFeedEntriesResponse>(
    `/cardfeeds/${cardFeedId}/cards?${params}`,
    requestConfig,
  )
  return response.data
}

export const postAssignCardFeedCards = async (
  cardFeedId: string,
  body: AssignCardFeedCardsRequest,
): Promise<AssignCardFeedCardsResponse> => {
  const requestConfig = getRequestConfig()
  const response = await axios.post<AssignCardFeedCardsResponse>(
    `/cardfeeds/${cardFeedId}/assigncards`,
    body,
    requestConfig,
  )
  return response.data
}

export const postCreateCardFeed = async (
  organizationId: string,
  body: CreateCardFeedRequest,
): Promise<CardFeedResponse> => {
  const requestConfig = getRequestConfig()
  const response = await axios.post<CardFeedResponse>(
    `/organizations/${organizationId}/cardfeeds`,
    body,
    requestConfig,
  )
  return response.data
}

// Reinvite a pending card-feed cardholder by re-sending the org invite. Reuses
// the same endpoint as the postInviteToOrganization thunk (organizations.actions),
// but stays intentionally thin — the domain hook owns mutation state and toasts.
// The invite response isn't consumed here, so the call resolves to void.
export const postReinviteCardholder = async (
  organizationId: string,
  body: PostPeopleInvitesRequest,
): Promise<void> => {
  const requestConfig = getRequestConfig()
  await axios.post(
    `/organizations/${organizationId}/invite`,
    body,
    requestConfig,
  )
}

// Unassign a single unconverted card from a feed. No request body — the caller
// and org are resolved server-side from the auth grant.
export const postUnassignCardFeedCard = async (
  cardFeedId: string,
  creditCardId: string,
): Promise<UnassignCardFeedCardResponse> => {
  const requestConfig = getRequestConfig()
  const response = await axios.post<UnassignCardFeedCardResponse>(
    `/cardfeeds/${cardFeedId}/cards/${creditCardId}/unassign`,
    undefined,
    requestConfig,
  )
  return response.data
}

export const patchCardFeed = async (
  organizationId: string,
  cardFeedId: string,
  body: UpdateCardFeedRequest,
): Promise<CardFeedResponse> => {
  const requestConfig = getRequestConfig()
  const response = await axios.patch<CardFeedResponse>(
    `/organizations/${organizationId}/cardfeeds/${cardFeedId}`,
    body,
    requestConfig,
  )
  return response.data
}
